import { useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { client } from 'utils/api-client'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from 'context/auth-context'
import { updateAuthCookie } from 'utils/tokenManager'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { Helmet } from 'react-helmet-async'
import { makeNumbersLatin } from 'utils/farsi'
interface IFormInputs {
  firstName: string
  lastName: string
  email: string
  password: string
}

export const SignUp = () => {
  const [showPassword, setShowPassword] = useState(false)
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<IFormInputs>()

  const navigate = useNavigate()
  const location = useLocation()
  const verificationCode = location.state.verifyCode
  const username = location.state.username
  const { userRegister } = useAuth()

  const onSubmitSignUp: SubmitHandler<IFormInputs> = async (options) => {
    try {
      const normalizedVerificationCode = makeNumbersLatin(
        String(verificationCode ?? ''),
      )
      const normalizedCellphone = makeNumbersLatin(String(username ?? ''))
      // Create customer
      const customerData = {
        grant_type: 'verification_Code',
        firstName: options.firstName,
        lastName: options.lastName,
        password: options.password,
        email: options.email,
        cellphoneVerificationCode: normalizedVerificationCode,
        cellphone: normalizedCellphone,
      }

      const customerResult = await client('customers', { data: customerData })

      // Get auth token
      const authData = {
        grant_type: 'verification_code',
        verificationCode: normalizedVerificationCode,
        username: makeNumbersLatin(String(customerResult.user.username)),
        cellphone: makeNumbersLatin(String(customerResult.cellphone)),
      }

      const authResult = await client('oauth/token', { data: authData })

      const authToken = {
        accessToken: authResult.access_token,
        refreshToken: authResult.refresh_token,
        expiresAt: Date.now() + authResult.expires_in * 1000, // Convert seconds to milliseconds
      }

      // Use centralized token management
      updateAuthCookie(authToken)

      // Fetch user data
      const userToken = authToken.accessToken
      const userData = await client('customer', { userToken })

      userRegister(userData)
      navigate('/')
    } catch (error) {
      console.error('Signup failed:', error)
      // Handle signup error appropriately
      throw error
    }
  }

  const handleShowPasswordClick = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div className='my-12'>
      <Helmet>
        <title className='text-xl font-semibold font-semibold'>
          {' '}
          ثبت نام در بوک استوری
        </title>
        <meta
          name='description'
          content='بوک استوری - هر کتاب یک داستان، هر داستان یک زندگی دوباره'
        />
      </Helmet>
      <form
        className='max-w-lg w-full mt-5 mx-auto px-4 sm:px-6'
        onSubmit={handleSubmit(onSubmitSignUp)}
      >
        <h1 className='text-bold text-xl font-semibold font-semibold text-center pb-2.5 mb-3 border-b border-solid border-[#4F6294]'>
          فرم ثبت نام
        </h1>
        <input
          type='text'
          placeholder='نام خود را وارد کنید (الزامی)'
          className='input-theme block box-border w-full rounded border border-solid py-2.5 px-4 mb-2 text-sm sm:text-base '
          {...register('firstName', { required: true })}
        />
        {errors.firstName && (
          <p className='text-[#ff3d71] before:inline before:content-["⚠"] text-sm mb-2'>
            {' '}
            وارد کردن نام الزامی است.
          </p>
        )}
        <input
          type='text'
          placeholder='نام خانوادگی خود را وارد کنید (الزامی)'
          className='input-theme block box-border w-full rounded border border-solid py-2.5 px-4 mb-2 text-sm sm:text-base '
          {...register('lastName', { required: true })}
        />
        {errors.lastName && (
          <p className='text-[#ff3d71] before:inline before:content-["⚠"] text-sm mb-2'>
            {' '}
            وارد کردن نام خانوادگی الزامی است.
          </p>
        )}
        <input
          type='email'
          placeholder='ایمیل خود را وارد کنید'
          className='input-theme block box-border w-full rounded border border-solid py-2.5 px-4 mb-2 text-sm sm:text-base '
          {...register('email', {
            pattern: {
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
              message: 'Invalid email address',
            },
          })}
        />
        {errors.email && (
          <p className='text-[#ff3d71] before:inline before:content-["⚠"] text-sm mb-2'>
            {' '}
            فرمت ایمیل معتبر نیست.
          </p>
        )}
        <div className='relative'>
          <input
            placeholder='رمز کاربری خود را وارد کنید (حداقل هشت کاراکتر)'
            type={showPassword ? 'text' : 'password'}
            className='input-theme block box-border w-full rounded border border-solid py-2.5 px-4 pl-12 mb-2 text-sm sm:text-base'
            {...register('password', {
              required: 'وارد کردن رمز کاربری الزامی است.',
              minLength: { value: 8, message: 'حداقل 8 کاراکتر وارد نمایید.' },
            })}
          />
          <button
            type='button'
            className='absolute inset-y-0 left-3 flex items-center'
            onClick={handleShowPasswordClick}
            aria-label='نمایش یا پنهان کردن رمز'
          >
            {showPassword ? (
              <FontAwesomeIcon
                icon={faEyeSlash}
                style={{ fontSize: 16, marginLeft: 12 }}
              />
            ) : (
              <FontAwesomeIcon
                icon={faEye}
                style={{ fontSize: 16, marginLeft: 12 }}
              />
            )}
          </button>
        </div>
        {errors.password && (
          <p className='text-[#ff3d71] before:inline before:content-["⚠"] mb-2 text-sm'>
            {' '}
            {errors.password.message}
          </p>
        )}
        <input
          className='w-full rounded text-white border-none mt-8 sm:mt-10 py-3 sm:py-4 px-4 text-base sm:text-lg md:text-xl font-bold tracking-wider active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
          style={{ backgroundColor: 'var(--accent-primary)' }}
          type='submit'
          value=' ثبت نام'
        />
      </form>
    </div>
  )
}
